import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export type ButtonVariant = 'primary' | 'brand' | 'secondary' | 'ghost' | 'danger' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  full?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
}

const SIZE_MAP: Record<ButtonSize, { h: number; px: number; fs: number; gap: number; ic: number }> = {
  sm: { h: 30, px: 10, fs: 13, gap: 6, ic: 14 },
  md: { h: 36, px: 14, fs: 14, gap: 7, ic: 16 },
  lg: { h: 44, px: 18, fs: 15, gap: 8, ic: 18 },
};

const VARIANT_MAP: Record<ButtonVariant, { bg: string; fg: string; bd: string }> = {
  primary:   { bg: 'var(--dg-accent-500)', fg: 'white', bd: 'var(--dg-accent-500)' },
  brand:     { bg: 'var(--dg-brand)',      fg: 'white', bd: 'var(--dg-brand)' },
  secondary: { bg: 'white',                fg: 'var(--dg-text-1)', bd: 'var(--dg-border-strong)' },
  ghost:     { bg: 'transparent',          fg: 'var(--dg-text-2)', bd: 'transparent' },
  danger:    { bg: 'var(--dg-danger)',     fg: 'white', bd: 'var(--dg-danger)' },
  soft:      { bg: 'var(--dg-accent-50)',  fg: 'var(--dg-accent-700)', bd: 'var(--dg-accent-100)' },
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  children,
  full,
  style,
  ...rest
}: ButtonProps) {
  const sz = SIZE_MAP[size];
  const v = VARIANT_MAP[variant];
  const filled = variant === 'primary' || variant === 'brand' || variant === 'danger';

  return (
    <button
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sz.gap,
        height: sz.h,
        padding: `0 ${sz.px}px`,
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.bd}`,
        borderRadius: 'var(--dg-r-md)',
        fontSize: sz.fs,
        fontWeight: 500,
        letterSpacing: '-0.005em',
        cursor: rest.disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        width: full ? '100%' : 'auto',
        boxShadow: filled
          ? '0 1px 2px rgba(15,23,42,.08), inset 0 1px 0 rgba(255,255,255,.12)'
          : 'none',
        opacity: rest.disabled ? 0.5 : 1,
        transition: 'background .12s, border-color .12s',
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={sz.ic} />}
      {children}
      {iconRight && <Icon name={iconRight} size={sz.ic} />}
    </button>
  );
}
